import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import { UserPlus } from 'lucide-react'
import { ProfileHero } from '../../components/profile/ProfileHero'
import { ProfileStats } from '../../components/profile/ProfileStats'
import type { ProfileStat } from '../../components/profile/buildProfileStats'
import { GroupPhoto } from '../../components/group/GroupPhoto'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import type { Group } from '../../types'

export type GroupViewerRole = 'member' | 'guest' | 'pending' | 'none'

interface Props {
  group: Group | null
  stats: ProfileStat[]
  statsLoading: boolean
  role: GroupViewerRole
  onInvite: () => void
  onGuestJoin: () => void
  onJoin: () => void
  /** The member-only settings menu, beside the Invite button. */
  settings: ReactNode
}

function GuestInviteText({ groupName }: { groupName: string }) {
  return (
    <fbt desc="Invite banner on the group cover image shown to anonymous guests">
      You've been invited to join <fbt:param name="groupName">{groupName}</fbt:param>
    </fbt>
  )
}

/** The profile-style group header: photo, name, counts, then Invite / Join and the settings gear. */
export function GroupHeader({ group, stats, statsLoading, role, onInvite, onGuestJoin, onJoin, settings }: Props) {
  // A near-empty group's main job is getting people in, so Invite is the primary action there.
  const inviteVariant = group && group.memberIds.length <= 2 ? 'default' : 'outline'

  let actions: ReactNode = null
  if (role === 'member') {
    actions = (
      <>
        <Button variant={inviteVariant} className="h-9 flex-1" onClick={onInvite} data-testid="group-invite-button">
          <UserPlus className="size-4" />
          {fbt('Invite', 'Button in the group page header that opens the invite-members dialog')}
        </Button>
        {settings}
      </>
    )
  } else if (role === 'guest') {
    actions = (
      <Button className="h-9 flex-1" onClick={onGuestJoin}>
        <fbt desc="CTA button in the guest invite banner overlaid on the group cover image">Register to join</fbt>
      </Button>
    )
  } else if (role === 'pending') {
    actions = (
      <Button className="h-9 flex-1" onClick={onJoin}>
        <fbt desc="Button to confirm joining the group as a full member from the post-signin dialog">Join group</fbt>
      </Button>
    )
  }

  return (
    <ProfileHero
      avatar={<GroupPhoto imageUrl={group?.imageUrl} loading={!group} />}
      actions={group ? actions : <Skeleton className="h-9 flex-1" />}
    >
      {group ? (
        <h1 className="text-xl font-semibold leading-tight break-words">{group.name}</h1>
      ) : (
        <Skeleton className="h-7 w-32" />
      )}
      {role === 'guest' && group && (
        <p className="mt-1 text-sm text-muted-foreground">
          <GuestInviteText groupName={group.name} />
        </p>
      )}
      <ProfileStats stats={stats} loading={!group || statsLoading} />
    </ProfileHero>
  )
}
